/**
 * Extract the leading HTTP status code from a FetchService error message
 * (formatted as `${status}: ${body}`); null when there is no leading status.
 */
export function getStatusFromError(error: unknown): number | null {
  if (!(error instanceof Error)) {
    return null;
  }
  const match = error.message.match(/^(\d{3}):/);
  return match ? Number(match[1]) : null;
}

/**
 * Convert HTTP status code to translation key for user-friendly error messages
 */
export function getErrorMessageKey(status: number): string {
  switch (status) {
    case 400:
      return 'HttpErrors.badRequest';
    case 401:
      return 'HttpErrors.unauthorized';
    case 403:
      return 'HttpErrors.forbidden';
    case 404:
      return 'HttpErrors.notFound';
    case 429:
      return 'HttpErrors.tooManyRequests';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'HttpErrors.serverError';
    default:
      return 'HttpErrors.generic';
  }
}

/**
 * Extract error message from API response, prioritizing status-based messages
 * Raw API errors are logged but not shown to users
 */
export async function extractErrorMessage(
  response: Response,
  translateFn: (key: string) => string,
  fallbackMessage?: string
): Promise<string> {
  // Log the raw error for debugging but don't expose it to users
  try {
    const errorData = await response.json();
    console.error('API Error Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      body: errorData,
    });
  } catch (parseError) {
    console.error('Could not parse API error response:', parseError);

    // Try to get text response for logging
    try {
      const errorText = await response.text();
      console.error('API Error Text:', errorText);
    } catch (textError) {
      console.error('Could not get error text:', textError);
    }
  }

  // Always return user-friendly message based on status
  const errorKey = getErrorMessageKey(response.status);
  return fallbackMessage || translateFn(errorKey);
}
